import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Globe } from 'lucide-react';
import dayjs from 'dayjs';

const SchedulePicker = ({
    scheduledAt,
    onScheduleChange,
    disabled = false
}) => {
    const [mode, setMode] = useState(scheduledAt ? 'schedule' : 'now'); // 'now' | 'schedule'
    const [date, setDate] = useState(scheduledAt ? dayjs(scheduledAt).format('YYYY-MM-DD') : '');
    const [time, setTime] = useState(scheduledAt ? dayjs(scheduledAt).format('HH:mm') : '');

    // Get user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Update parent when date/time changes
    useEffect(() => {
        if (mode === 'now') {
            onScheduleChange(null);
        } else if (date && time) {
            const dateTime = dayjs(`${date} ${time}`);
            if (dateTime.isValid()) {
                onScheduleChange(dateTime.toISOString());
            }
        }
    }, [mode, date, time, onScheduleChange]);

    // Set default date/time when switching to schedule mode
    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === 'schedule' && !date) {
            // Default to tomorrow at 10:00 AM
            const tomorrow = dayjs().add(1, 'day');
            setDate(tomorrow.format('YYYY-MM-DD'));
            setTime('10:00');
        }
    };

    // Get minimum date (today)
    const minDate = dayjs().format('YYYY-MM-DD');

    // Get minimum time if date is today
    const isToday = date === minDate;
    const minTime = isToday ? dayjs().add(5, 'minute').format('HH:mm') : undefined;

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                When to post
            </label>

            {/* Mode toggle */}
            <div className="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1">
                <button
                    type="button"
                    onClick={() => handleModeChange('now')}
                    disabled={disabled}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                        mode === 'now'
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    Post Now
                </button>
                <button
                    type="button"
                    onClick={() => handleModeChange('schedule')}
                    disabled={disabled}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                        mode === 'schedule'
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    Schedule
                </button>
            </div>

            {/* Date/Time pickers */}
            {mode === 'schedule' && (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        {/* Date picker */}
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                min={minDate}
                                disabled={disabled}
                                className={`w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            />
                        </div>

                        {/* Time picker */}
                        <div className="relative">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                                type="time"
                                value={time}
                                onChange={(e) => setTime(e.target.value)}
                                min={minTime}
                                disabled={disabled}
                                className={`w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            />
                        </div>
                    </div>

                    {/* Timezone indicator */}
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Globe className="w-3.5 h-3.5" />
                        <span>Timezone: {timezone}</span>
                    </div>

                    {/* Schedule preview */}
                    {date && time && (
                        <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                            <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                Will be posted on{' '}
                                <span className="font-medium">
                                    {dayjs(`${date} ${time}`).format('dddd, MMMM D, YYYY [at] h:mm A')}
                                </span>
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SchedulePicker;
